import { Button, Card, Section, Tabs, Textarea, useToast } from '@memorizecards/ui';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CardHtml } from '../components/CardHtml';
import { IconNotebook } from '../components/Icons';
import { VisualizarCardModal } from '../components/VisualizarCardModal';
import { Carregando, Erro, TituloPagina, Vazio } from '../components/estado';
import { formatNumber, labelDoCaminho } from '../lib/format';
import { useCaderno, useNotas, useSalvarNota } from '../lib/queries';

export function Caderno(): ReactNode {
  return (
    <Section spacing="md">
      <TituloPagina
        eyebrow="Suas anotações"
        titulo="Caderno"
        descricao="Tudo o que você anotou nos cards, num lugar só. O sorteio serve para revisar sem escolher."
      />

      <Tabs
        items={[
          { id: 'sorteio', label: 'Revisar sorteando', content: <Sorteio /> },
          { id: 'lista', label: 'Todas as anotações', content: <Lista /> },
        ]}
      />
    </Section>
  );
}

function Sorteio(): ReactNode {
  const { data, isPending, error, refetch, isFetching } = useCaderno();
  const salvar = useSalvarNota();
  const toast = useToast();
  const [rascunho, setRascunho] = useState('');

  useEffect(() => {
    setRascunho(data?.texto ?? '');
  }, [data?.texto, data?.card?.id]);

  if (isPending) return <Carregando />;
  if (error) return <Erro error={error} aoTentarNovamente={() => void refetch()} />;

  if (!data || data.total === 0 || !data.card) {
    return (
      <Vazio
        titulo="Seu caderno está vazio"
        descricao="Enquanto estuda, clique no ícone de caderno em qualquer card para anotar o que não pode esquecer."
        acao={
          <Link to="/baralhos">
            <Button>Ir estudar</Button>
          </Link>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="mc-mono">
          {formatNumber(data.total)}{' '}
          {data.total === 1 ? 'anotação' : 'anotações'} no caderno
        </p>
        <Button variant="ghost" size="sm" loading={isFetching} onClick={() => void refetch()}>
          Sortear outra
        </Button>
      </div>

      <Card padding="lg">
        {data.card.caminho.length > 0 ? (
          <p className="mc-mono mb-4 normal-case tracking-normal">
            {data.card.caminho.slice(2).join(' › ') || data.card.caminho.join(' › ')}
          </p>
        ) : null}

        <CardHtml html={data.card.pergunta || data.card.frente} className="text-lg" />
        <hr className="mc-rule my-6" />
        <CardHtml html={data.card.verso} />
      </Card>

      <Card padding="lg">
        <div className="mb-4 flex items-center gap-2 text-mc-accent">
          <IconNotebook className="size-5" />
          <h2 className="text-mc-card-title font-medium">Sua anotação</h2>
        </div>

        <Textarea
          value={rascunho}
          onChange={(e) => setRascunho(e.target.value)}
          className="min-h-40"
          maxLength={8000}
          label="Anotação"
        />

        <div className="mt-4 flex flex-wrap justify-end gap-3">
          <Link to={`/estudar?deck=${data.card.deckId}`}>
            <Button variant="ghost">Estudar esta matéria</Button>
          </Link>
          <Button
            loading={salvar.isPending}
            onClick={() =>
              salvar.mutate(
                { cardId: data.card!.id, texto: rascunho },
                {
                  onSuccess: (r) => {
                    toast.success(r.removida ? 'Anotação removida.' : 'Anotação salva.');
                    void refetch();
                  },
                  onError: () => toast.error('Não consegui salvar.'),
                },
              )
            }
          >
            Salvar
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Lista(): ReactNode {
  const [page, setPage] = useState(1);
  const [cardVer, setCardVer] = useState<number | null>(null);
  const { data, isPending, error, refetch } = useNotas(page);

  if (isPending) return <Carregando />;
  if (error) return <Erro error={error} aoTentarNovamente={() => void refetch()} />;
  if (!data || data.total === 0) {
    return (
      <Vazio
        titulo="Nenhuma anotação ainda"
        descricao="As anotações que você escrever nos cards aparecem aqui."
      />
    );
  }

  const paginas = Math.ceil(data.total / 25);

  return (
    <div className="flex flex-col gap-5">
      <p className="mc-mono">
        {formatNumber(data.total)} {data.total === 1 ? 'anotação' : 'anotações'}
      </p>

      <ul className="flex flex-col gap-4">
        {data.itens.map((nota) => (
          <li key={nota.cardId}>
            <Card padding="md" className="flex flex-col gap-3">
              <p className="mc-mono normal-case tracking-normal">
                {labelDoCaminho(nota.frente)}
              </p>
              <p className="whitespace-pre-wrap break-words text-sm text-mc-ink2">
                {nota.texto}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setCardVer(nota.cardId)}
                  className="text-sm text-mc-accent underline underline-offset-4"
                >
                  Ver card
                </button>
                <Link
                  to={`/estudar?deck=${nota.deckId}`}
                  className="text-sm text-mc-accent underline underline-offset-4"
                >
                  Estudar esta matéria
                </Link>
              </div>
            </Card>
          </li>
        ))}
      </ul>

      <Paginacao pagina={page} paginas={paginas} aoMudar={setPage} />

      <VisualizarCardModal
        cardId={cardVer}
        open={cardVer !== null}
        onClose={() => setCardVer(null)}
      />
    </div>
  );
}

export function Paginacao({
  pagina,
  paginas,
  aoMudar,
}: {
  pagina: number;
  paginas: number;
  aoMudar: (p: number) => void;
}): ReactNode {
  if (paginas <= 1) return null;

  return (
    <nav className="flex items-center justify-center gap-3" aria-label="Paginação">
      <Button
        variant="ghost"
        size="sm"
        disabled={pagina <= 1}
        onClick={() => aoMudar(pagina - 1)}
      >
        Anterior
      </Button>
      <span className="mc-mono tabular-nums">
        {pagina} de {paginas}
      </span>
      <Button
        variant="ghost"
        size="sm"
        disabled={pagina >= paginas}
        onClick={() => aoMudar(pagina + 1)}
      >
        Próxima
      </Button>
    </nav>
  );
}
